/**
 * Appearance = which of the three looks the app wears, plus light/dark.
 *
 * - **Look** (calm | familiar | focus) is chosen by admins and applies to everyone: a workspace's own choice wins,
 *   else the system default set by a global admin, else calm. The tokens for each look live in
 *   `src/styles/tokens.css` under `:root[data-look=…]`; DESIGN.md describes them.
 * - **Theme** (light | dark | auto) is each person's own choice, kept only in their browser. `auto` follows the device.
 *
 * The last look and theme are cached in localStorage so `messaging.html` can set them before the first paint (no
 * flash of the default look); keep its inline script in step with STORAGE_KEY and LOOK_FONTS here.
 */
export const LOOKS = ['calm', 'familiar', 'focus'] as const;
export type Look = (typeof LOOKS)[number];
export type ThemePref = 'light' | 'dark' | 'auto';
export type Theme = 'light' | 'dark';

export const DEFAULT_LOOK: Look = 'calm';
// Follows the device until the person picks Light or Dark (user menu → Appearance).
export const DEFAULT_THEME: ThemePref = 'auto';
export const STORAGE_KEY = 'mf.appearance';

/** Fonts beyond Manrope (loaded by messaging.html for every look). All three include Cyrillic Ө/Ү (cyrillic-ext). */
export const LOOK_FONTS: Record<Look, string | null> = {
  calm: null,
  familiar: 'https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800&display=swap',
  focus: 'https://fonts.googleapis.com/css2?family=Golos+Text:wght@400;500;600;700&display=swap',
};

export function parseLook(value: unknown): Look | null {
  return typeof value === 'string' && (LOOKS as readonly string[]).includes(value) ? (value as Look) : null;
}

function parseThemePref(value: unknown): ThemePref | null {
  return value === 'light' || value === 'dark' || value === 'auto' ? value : null;
}

/** Workspace look, else system default, else calm. A value this build doesn't know (a retired look) counts as unset. */
export function effectiveLook(tenant: unknown, system: unknown): { look: Look; source: 'tenant' | 'system' | 'default' } {
  const t = parseLook(tenant);
  if (t) return { look: t, source: 'tenant' };
  const s = parseLook(system);
  if (s) return { look: s, source: 'system' };
  return { look: DEFAULT_LOOK, source: 'default' };
}

export function resolveTheme(pref: ThemePref, prefersDark: boolean): Theme {
  if (pref === 'auto') return prefersDark ? 'dark' : 'light';
  return pref;
}

export interface CachedAppearance {
  look: Look;
  theme: ThemePref;
}

/** Storage can be missing or throw (private mode, blocked site data): every read falls back to the defaults. */
export function readCached(storage: Pick<Storage, 'getItem'> | null | undefined): CachedAppearance {
  try {
    const raw = storage?.getItem(STORAGE_KEY);
    const parsed = raw ? (JSON.parse(raw) as { look?: unknown; theme?: unknown }) : {};
    return { look: parseLook(parsed.look) ?? DEFAULT_LOOK, theme: parseThemePref(parsed.theme) ?? DEFAULT_THEME };
  } catch {
    return { look: DEFAULT_LOOK, theme: DEFAULT_THEME };
  }
}

export function writeCached(storage: Pick<Storage, 'setItem'> | null | undefined, value: CachedAppearance): void {
  try {
    storage?.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // A full or blocked storage only costs the pre-paint cache; the app still applies the look itself.
  }
}

export function applyAppearance(root: Pick<Element, 'setAttribute'>, look: Look, theme: Theme): void {
  root.setAttribute('data-look', look);
  root.setAttribute('data-theme', theme);
}

/** Load the look's font stylesheet once (Manrope, calm's font, is always in messaging.html). */
export function ensureLookFont(
  doc: {
    head: { querySelector(sel: string): unknown; appendChild(el: never): unknown };
    createElement(tag: 'link'): { rel: string; href: string };
  },
  look: Look,
): void {
  const href = LOOK_FONTS[look];
  if (!href || doc.head.querySelector(`link[href="${href}"]`)) return;
  const link = doc.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  doc.head.appendChild(link as never);
}
